import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { backgroundsService } from '@/services/backgrounds.service';
import type { BackgroundUpsert } from '@/types/backgrounds';
import type { SORT_ORDER } from '@/types';

export type BackgroundListParams = {
  cursor?: string;
  limit?: number;
  sortBy?: 'startDate';
  sortOrder?: SORT_ORDER | 'ASC' | 'DESC';
};

export const useBackgrounds = (params: BackgroundListParams = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.BACKGROUNDS, params],
    queryFn: () => backgroundsService.list(params),
  });
};

export const useBackgroundById = (id: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.BACKGROUNDS, id],
    queryFn: () => backgroundsService.getById({ id: id ?? '' }),
    enabled: !!id,
  });
};

export const useCreateBackgroundMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BackgroundUpsert) => backgroundsService.create({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BACKGROUNDS] });
      showNotification({ title: 'Success', message: 'Background created successfully', color: 'green' });
    },
  });
};

export const useUpdateBackgroundMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BackgroundUpsert }) => backgroundsService.update({ id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BACKGROUNDS] });
      showNotification({ title: 'Success', message: 'Background updated successfully', color: 'green' });
    },
  });
};

export const useRemoveBackgroundMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backgroundsService.remove({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BACKGROUNDS] });
      showNotification({ title: 'Success', message: 'Background removed successfully', color: 'green' });
    },
  });
};
